import {
  BarChart3,
  Boxes,
  DatabaseBackup,
  FileSpreadsheet,
  PieChart,
  ClipboardCheck,
  Building2,
  Cog,
  Warehouse,
  MapPin,
  MapPinned,
  ScanLine,
  DoorOpen,
  Folder,
  Gauge,
  History,
  Layers,
  LayoutDashboard,
  Package,
  Percent,
  PenTool,
  Split,
  Route,
  Truck,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import type { Action, Resource } from '@mouldtrack/shared';
import type { ManagedTable } from '../../db/queries.js';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Hidden entirely when the role lacks this permission - never shown disabled. */
  permission?: { action: Action; resource: Resource };
  /** Short label for the mobile tab bar, where space is measured in characters. */
  shortLabel?: string;
}

export interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
}

/**
 * Master-table registry.
 *
 * One declaration drives the sidebar entry, the route, the screen title and the
 * form. Eleven near-identical CRUD screens hand-written eleven times is eleven
 * places for them to drift apart.
 */
export interface MasterDefinition {
  table: ManagedTable;
  path: string;
  title: string;
  singular: string;
  icon: LucideIcon;
  description: string;
  /**
   * Which permission controls this screen. The catalog masters share one
   * resource; locations, stores and checkpoints each have their own, because a
   * role can be allowed to record where things are without being allowed to
   * invent new places to put them.
   */
  resource?: Resource;
  /**
   * False for tables with no `isActive` column. Sending one would be a field the
   * server does not have, and offering the checkbox would promise a soft-delete
   * that never happens.
   */
  hasActiveFlag?: boolean;
  /** Fields beyond the universal code/name/isActive. */
  extraFields?: Array<{
    name: string;
    label: string;
    type: 'text' | 'number' | 'textarea' | 'select';
    placeholder?: string;
    hint?: string;
    required?: boolean;
    options?: Array<{ value: string; label: string }>;
    /** Pull options from another master table at render time. */
    optionsFrom?: ManagedTable;
  }>;
  /** Extra columns in the list, beyond code/name/status. */
  extraColumns?: Array<{ key: string; label: string }>;
  codePlaceholder?: string;
  codeHint?: string;
}

export const MASTER_DEFINITIONS: MasterDefinition[] = [
  {
    table: 'companies',
    path: 'companies',
    title: 'Companies',
    singular: 'Company',
    icon: Building2,
    description: 'Mould owners and manufacturers. The owner is engraved on the nameplate.',
    codePlaceholder: 'FERRERO',
    extraFields: [
      {
        name: 'logoSvg',
        label: 'Logo (SVG markup)',
        type: 'textarea',
        hint: 'Vector logo used on the engraving nameplate. Paste the SVG source.',
      },
    ],
  },
  {
    table: 'projects',
    path: 'projects',
    title: 'Projects',
    singular: 'Project',
    icon: Folder,
    description: 'The project code is the prefix of every mould ID allocated under it.',
    codePlaceholder: '2170',
    codeHint: 'Numeric prefix - mould IDs become 2170501, 2170502, …',
    extraFields: [
      {
        name: 'ownerCompanyId',
        label: 'Owner company',
        type: 'select',
        optionsFrom: 'companies',
      },
    ],
    extraColumns: [{ key: 'ownerCompanyName', label: 'Owner' }],
  },
  {
    table: 'designers',
    path: 'designers',
    title: 'Designers',
    singular: 'Designer',
    icon: PenTool,
    description: 'Tool designers credited on the mould record.',
  },
  {
    table: 'toolTypes',
    path: 'tool-types',
    title: 'Tool Types',
    singular: 'Tool Type',
    icon: Wrench,
    description: 'Injection, compression, blow, and the rest of the tool families.',
  },
  {
    table: 'runnerTypes',
    path: 'runner-types',
    title: 'Runner Types',
    singular: 'Runner Type',
    icon: Split,
    description: 'Hot, cold or insulated runner. Printed on the nameplate beside the cavity count.',
  },
  {
    table: 'gateTypes',
    path: 'gate-types',
    title: 'Gate Types',
    singular: 'Gate Type',
    icon: DoorOpen,
    description: 'Pin point, edge, submarine, direct.',
  },
  {
    table: 'ejectionTypes',
    path: 'ejection-types',
    title: 'Ejection Types',
    singular: 'Ejection Type',
    icon: Layers,
    description: 'Ejector pin, stripper plate, air, and combinations.',
  },
  {
    table: 'materials',
    path: 'materials',
    title: 'Materials',
    singular: 'Material',
    icon: Boxes,
    description: 'Steel grades used for core/cavity and mould base.',
  },
  {
    table: 'materialGrades',
    path: 'material-grades',
    title: 'Material Grades',
    singular: 'Material Grade',
    icon: Percent,
    description: 'Moulding material and its shrinkage - e.g. PP at 1.5%.',
    extraFields: [
      {
        name: 'shrinkagePct',
        label: 'Shrinkage %',
        type: 'number',
        placeholder: '1.5',
        hint: 'Percentage, as written on the spec sheet.',
      },
    ],
    extraColumns: [{ key: 'shrinkagePct', label: 'Shrinkage %' }],
  },
  {
    table: 'mechanismTypes',
    path: 'mechanism-types',
    title: 'Mechanisms',
    singular: 'Mechanism',
    icon: Cog,
    description: 'Slider and lifter configurations, e.g. "2 sliders", "4 lifters".',
  },
];

/**
 * Sites, stores and scan points.
 *
 * The scanner offers exactly the checkpoints listed here, so without these
 * screens the only checkpoints on the floor are whatever the seed script put
 * there - which is how a demo "Pune Plant 1" ends up on a shop floor in Mira
 * Road, with no way to remove it from inside the product.
 */
export const PLACE_DEFINITIONS: MasterDefinition[] = [
  {
    table: 'locations',
    path: 'locations',
    title: 'Locations',
    singular: 'Location',
    icon: MapPin,
    resource: 'locations',
    hasActiveFlag: false,
    description: 'Your plants, warehouses and vendor sites.',
    codePlaceholder: 'LOC-MIRA-ROAD',
    extraFields: [
      {
        name: 'type',
        label: 'Type',
        type: 'select',
        required: true,
        options: [
          { value: 'PLANT', label: 'Plant' },
          { value: 'WAREHOUSE', label: 'Warehouse' },
          { value: 'VENDOR', label: 'Vendor' },
          { value: 'CUSTOMER', label: 'Customer' },
        ],
      },
      { name: 'address', label: 'Address', type: 'textarea' },
    ],
    extraColumns: [{ key: 'type', label: 'Type' }],
  },
  {
    table: 'stores',
    path: 'stores',
    title: 'Stores',
    singular: 'Store',
    icon: Warehouse,
    resource: 'stores',
    hasActiveFlag: false,
    description: 'Racks and rooms inside a location where moulds are kept.',
    codePlaceholder: 'STR-A1',
    extraFields: [
      {
        name: 'locationId',
        label: 'Location',
        type: 'select',
        required: true,
        optionsFrom: 'locations',
        hint: 'Which site this store is inside.',
      },
    ],
  },
  {
    table: 'checkpoints',
    path: 'checkpoints',
    title: 'Checkpoints',
    singular: 'Checkpoint',
    icon: ScanLine,
    resource: 'checkpoints',
    hasActiveFlag: false,
    description: 'The scan points the Android app offers. Every scan happens at one of these.',
    codePlaceholder: 'CHK-MIRA-ROAD',
    codeHint: 'Scanned or typed on the handheld, so keep it short.',
    extraFields: [
      {
        name: 'type',
        label: 'Type',
        type: 'select',
        required: true,
        hint: 'LOCATION simply records where a mould is now. The others also move its status.',
        options: [
          { value: 'LOCATION', label: 'Location - records where it is' },
          { value: 'STORE_IN', label: 'Store in' },
          { value: 'STORE_OUT', label: 'Store out' },
          { value: 'GATE_IN', label: 'Gate in' },
          { value: 'GATE_OUT', label: 'Gate out' },
          { value: 'TRANSIT', label: 'In transit' },
          { value: 'QC', label: 'QC inspection' },
          { value: 'MAINTENANCE', label: 'Maintenance' },
        ],
      },
      {
        name: 'locationId',
        label: 'Location',
        type: 'select',
        required: true,
        optionsFrom: 'locations',
      },
      {
        name: 'storeId',
        label: 'Store',
        type: 'select',
        optionsFrom: 'stores',
        hint: 'Only for a checkpoint at a specific rack or room.',
      },
    ],
    extraColumns: [{ key: 'type', label: 'Type' }],
  },
];

export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'main',
    label: 'Catalog',
    items: [
      { to: '/', label: 'Overview', shortLabel: 'Home', icon: LayoutDashboard },
      // Second only to Overview, and first on the mobile tab bar that matters.
      // For a supervisor this is the whole application; burying it under the
      // catalog would optimise the navigation for the people who use it least.
      {
        to: '/scan',
        label: 'Scan',
        shortLabel: 'Scan',
        icon: ScanLine,
        permission: { action: 'read', resource: 'scans' },
      },
      {
        to: '/moulds',
        label: 'Mould Catalog',
        shortLabel: 'Moulds',
        icon: Package,
        permission: { action: 'read', resource: 'moulds' },
      },
      {
        to: '/tracking',
        label: 'Tracking',
        shortLabel: 'Track',
        icon: MapPinned,
        permission: { action: 'read', resource: 'scans' },
      },
      {
        to: '/transfers',
        label: 'Transfers',
        shortLabel: 'Moves',
        icon: Truck,
        permission: { action: 'read', resource: 'transfers' },
      },
      {
        to: '/journeys',
        label: 'Journeys',
        shortLabel: 'Trips',
        icon: Route,
        permission: { action: 'read', resource: 'journeys' },
      },
      {
        to: '/maintenance',
        label: 'Maintenance',
        shortLabel: 'Service',
        icon: Wrench,
        permission: { action: 'read', resource: 'moulds' },
      },
      {
        to: '/stock-take',
        label: 'Stock Take',
        shortLabel: 'Count',
        icon: ClipboardCheck,
        permission: { action: 'read', resource: 'scans' },
      },
      {
        to: '/reports',
        label: 'Reports',
        shortLabel: 'Reports',
        icon: BarChart3,
        permission: { action: 'read', resource: 'reports' },
      },

      {
        to: '/data-quality',
        label: 'Data Quality',
        shortLabel: 'Quality',
        icon: Gauge,
        permission: { action: 'read', resource: 'moulds' },
      },
    ],
  },
  // Its own section, not the catalog's: the first section is also the mobile
  // tab bar, and it is full.
  {
    id: 'insight',
    label: 'Analysis & Data',
    items: [
      {
        to: '/analysis',
        label: 'Analysis',
        shortLabel: 'Analysis',
        icon: PieChart,
        permission: { action: 'read', resource: 'reports' },
      },
      {
        to: '/imports',
        label: 'Import history',
        shortLabel: 'Imports',
        icon: FileSpreadsheet,
        permission: { action: 'create', resource: 'moulds' },
      },
    ],
  },
  {
    id: 'places',
    label: 'Sites & Scanning',
    items: PLACE_DEFINITIONS.map((m) => ({
      to: `/masters/${m.path}`,
      label: m.title,
      icon: m.icon,
      permission: { action: 'read', resource: m.resource! },
    })),
  },
  {
    id: 'masters',
    label: 'Master Data',
    items: [
      {
        to: '/masters',
        label: 'Master Data',
        shortLabel: 'Data',
        icon: Boxes,
        permission: { action: 'read', resource: 'catalog-masters' as Resource },
      },
    ],
  },
  {
    id: 'admin',
    label: 'Administration',
    items: [
      {
        to: '/people',
        label: 'People',
        shortLabel: 'People',
        icon: Users,
        // Only ADMIN holds `users`, so this entry is invisible to everyone else.
        permission: { action: 'read', resource: 'users' },
      },
      {
        to: '/activity',
        label: 'Activity log',
        shortLabel: 'Activity',
        icon: History,
        // Wider than People on purpose. A trail that only administrators can
        // read answers "what happened" but not "who is watching the watchers",
        // and the second is usually why a log gets asked for.
        permission: { action: 'read', resource: 'scans' },
      },
      {
        to: '/backups',
        label: 'Backups',
        shortLabel: 'Backups',
        icon: DatabaseBackup,
        // Administrators only: a backup holds every record in the system.
        permission: { action: 'read', resource: 'backups' },
      },
    ],
  },
];

/** Every screen the generic master list/form can drive. */
export const ALL_MANAGED_DEFINITIONS: MasterDefinition[] = [
  ...MASTER_DEFINITIONS,
  ...PLACE_DEFINITIONS,
];
